"use client";

import React, {
  createContext,
  useContext,
  useState,
  useCallback,
  useEffect,
} from "react";
import { classed } from "@/lib/classed";
import { Text } from "./text";
import { Icon } from "./icons";
import { VStack } from "./v-stack";
interface Toast {
  id: string;
  message: string;
  type: "success" | "error" | "warning" | "info";
  duration?: number;
}

interface ToastContextType {
  toasts: Toast[];
  addToast: (message: string, type: Toast["type"], duration?: number) => void;
  removeToast: (id: string) => void;
}

const ToastContext = createContext<ToastContextType | undefined>(undefined);

export function useToast() {
  const context = useContext(ToastContext);
  if (!context) {
    throw new Error("useToast must be used within a ToastProvider");
  }
  return context;
}

export function ToastProvider({ children }: { children: React.ReactNode }) {
  const [toasts, setToasts] = useState<Toast[]>([]);
  const counterRef = React.useRef(0);

  const removeToast = useCallback((id: string) => {
    setToasts((prev) => prev.filter((toast) => toast.id !== id));
  }, []);

  const addToast = useCallback(
    (message: string, type: Toast["type"], duration = 5000) => {
      counterRef.current += 1;
      const id = `${Date.now()}-${counterRef.current}`;
      const newToast: Toast = { id, message, type, duration };

      setToasts((prev) => [...prev, newToast]);
    },
    [],
  );

  return (
    <ToastContext.Provider value={{ toasts, addToast, removeToast }}>
      {children}
      <ToastContainer toasts={toasts} removeToast={removeToast} />
    </ToastContext.Provider>
  );
}

interface ToastContainerProps {
  toasts: Toast[];
  removeToast: (id: string) => void;
}

function ToastContainer({ toasts, removeToast }: ToastContainerProps) {
  if (toasts.length === 0) return null;

  return (
    <VStack gap="sm" className="fixed top-4 right-4 z-overlay">
      {toasts.map((toast) => (
        <ToastItem
          key={toast.id}
          toast={toast}
          onClose={() => removeToast(toast.id)}
        />
      ))}
    </VStack>
  );
}

interface ToastItemProps {
  toast: Toast;
  onClose: () => void;
}

const ToastShell = classed("div", {
  base: "flex items-center justify-between p-4 rounded-lg border anim-toast-enter",
  variants: {
    variant: {
      success:
        "bg-[color:var(--accent-green-bg)] border-[color:var(--accent-green)] text-[color:var(--accent-green)]",
      error:
        "bg-[color:var(--error-container)] border-[color:var(--error)] text-[color:var(--error)]",
      warning:
        "bg-[color:var(--accent-yellow-bg)] border-[color:var(--accent-yellow)] text-[color:var(--accent-yellow)]",
      info: "bg-[color:var(--primary-container)] border-[color:var(--primary)] text-[color:var(--primary)]",
    },
  },
  defaultVariants: {
    variant: "info",
  },
});

function ToastItem({ toast, onClose }: ToastItemProps) {
  const duration = toast.duration ?? 5000;

  useEffect(() => {
    if (duration <= 0) return;

    let remaining = duration;
    let start = Date.now();
    let timer: ReturnType<typeof setTimeout>;

    const tick = () => {
      timer = setTimeout(() => {
        onClose();
      }, remaining);
      start = Date.now();
    };

    const handleVisibility = () => {
      if (document.visibilityState === "hidden") {
        clearTimeout(timer);
        remaining -= Date.now() - start;
      } else {
        tick();
      }
    };

    tick();
    document.addEventListener("visibilitychange", handleVisibility);
    return () => {
      clearTimeout(timer);
      document.removeEventListener("visibilitychange", handleVisibility);
    };
  }, [duration, onClose]);

  return (
    <ToastShell
      variant={toast.type}
      role="alert"
      aria-live={toast.type === "error" ? "assertive" : "polite"}
      className="shadow-lg"
    >
      <Text weight="medium" color="inherit" className="flex-1">
        {toast.message}
      </Text>
      <button
        onClick={onClose}
        className="ml-4 p-1 hover:bg-[color:var(--on-surface)]/5 rounded transition-colors"
        aria-label="Close notification"
      >
        <Icon.X size="sm" />
      </button>
    </ToastShell>
  );
}
